// ContractTemplateEditor.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Document, Page, pdfjs } from "react-pdf";
import "../css/ContractEditor.css";
import {
  PenLine,
  Mail,
  User2,
  Home,
  CalendarDays,
  Type,
  ZoomIn,
  ZoomOut,
  Grid as GridIcon,
  Upload,
  Save,
  Eye,
  Send,
  Trash2,
  SunMedium,
  Moon,
  Move,
  CheckCircle,
  FolderOpen,
} from "lucide-react";
import { PDFDocument } from "pdf-lib";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const PAGE_W = 794; // logical PDF display width in px
const PAGE_H = 1123; // logical PDF display height in px

const FIELD_PRESETS = [
  { type: "name", label: "Name", icon: User2 },
  { type: "email", label: "Email", icon: Mail },
  { type: "address", label: "Address", icon: Home },
  { type: "signature", label: "Signature", icon: PenLine },
  { type: "date", label: "Date", icon: CalendarDays },
  { type: "text", label: "Text", icon: Type },
];

function randomId() {
  return Math.random().toString(36).slice(2, 9);
}

// Mock backend functions (unchanged)
async function savePdfToBackend(pdfBytes, fileName) {
  await new Promise((r) => setTimeout(r, 1500));
  const s3FilePath = `s3://your-bucket-name/templates/${fileName.replace(/\s/g, "_")}_${Date.now()}.pdf`;
  console.log(`[BACKEND MOCK] Saved ${fileName} to: ${s3FilePath}`);
  return { success: true, filePath: s3FilePath };
}

async function getPdfFromBackend(filePath) {
  await new Promise((r) => setTimeout(r, 500));
  const signedUrl = `https://mock-signed-url.com/${encodeURIComponent(filePath)}.pdf`;
  console.log(`[BACKEND MOCK] Retrieving signed URL for: ${filePath}`);
  return { success: true, signedUrl };
}

export default function ContractTemplateEditor() {
  const [dark, setDark] = useState(true);
  const [scale, setScale] = useState(1.0); // you can change this to zoom in/out
  const [grid, setGrid] = useState(true);
  const [snap, setSnap] = useState(true);
  const [pages, setPages] = useState([]); // will hold { id: 'page_1' } objects
  const [fields, setFields] = useState([]);
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [uploadName, setUploadName] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [lastSavedS3Path, setLastSavedS3Path] = useState(null);

  // pageRef is the scrollable container that holds all pages (and where fields are placed)
  const pageRef = useRef(null);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  const scaled = useMemo(() => ({ w: PAGE_W * scale, h: PAGE_H * scale }), [scale]);

  function onDragStartPalette(e, preset) {
    e.dataTransfer.setData("application/x-field", JSON.stringify(preset));
    e.dataTransfer.effectAllowed = "copy";
  }

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  // Convert client (screen) coordinates to { page: idx, x: unscaledX, y: unscaledY }
  function clientToPageXY(clientX, clientY) {
    const container = pageRef.current;
    if (!container) return { page: 0, x: 0, y: 0 };

    const rect = container.getBoundingClientRect();
    // include scrollTop because pages are stacked vertically inside this container
    const scrollTop = container.scrollTop || 0;
    const scrollLeft = container.scrollLeft || 0;

    // position within the whole stacked area (in screen px)
    const xTotal = clientX - rect.left + scrollLeft;
    const yTotal = clientY - rect.top + scrollTop;

    const pageHeightScreen = PAGE_H * scale;
    const pageWidthScreen = PAGE_W * scale;

    let pageIndex = Math.floor(yTotal / pageHeightScreen);
    if (pageIndex < 0) pageIndex = 0;
    if (pageIndex >= pages.length) pageIndex = Math.max(0, pages.length - 1);

    // position inside the page (screen px)
    const yInPageScreen = yTotal - pageIndex * pageHeightScreen;
    const xInPageScreen = xTotal; // assume no horizontal stacking

    // convert back to unscaled PDF units
    const xPdf = xInPageScreen / scale;
    const yPdf = yInPageScreen / scale;

    // clamp to page bounds in unscaled units
    const xClamped = clamp(xPdf, 0, PAGE_W);
    const yClamped = clamp(yPdf, 0, PAGE_H);

    return { page: pageIndex, x: xClamped, y: yClamped };
  }

  function addFieldAt(preset, posWithPage) {
    const { page, x, y } = posWithPage;
    const baseW = preset.type === "signature" ? 260 : 240;
    const baseH = preset.type === "address" ? 90 : preset.type === "signature" ? 56 : 44;
    const f = {
      id: randomId(),
      type: preset.type,
      label: preset.label,
      page: page, // store 0-based page index
      x: clamp(x - baseW / 2, 0, PAGE_W - baseW),
      y: clamp(y - baseH / 2, 0, PAGE_H - baseH),
      w: baseW,
      h: baseH,
      required: true,
      fontSize: 14,
      color: "#2563eb",
      placeholder: preset.label,
    };
    setFields((cur) => [...cur, f]);
    setActiveFieldId(f.id);
  }

  function onDropCanvas(e) {
    e.preventDefault();
    const data = e.dataTransfer.getData("application/x-field");
    if (!data) return;
    const preset = JSON.parse(data);
    const pos = clientToPageXY(e.clientX, e.clientY);
    addFieldAt(preset, pos);
  }

  function onDragOverCanvas(e) {
    e.preventDefault();
  }

  // Field dragging within page
  const dragInfo = useRef(null);

  function startDragField(e, field) {
    e.stopPropagation();
    setActiveFieldId(field.id);

    // compute mouse position relative to page coordinate system (unscaled)
    const pos = clientToPageXY(e.clientX, e.clientY);

    // Ensure we start dragging the field's stored page (don't allow starting drag to implicitly change page)
    const startX = pos.x;
    const startY = pos.y;
    dragInfo.current = {
      id: field.id,
      page: field.page,
      startMouseX: startX,
      startMouseY: startY,
      origX: field.x,
      origY: field.y,
    };

    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", endDrag);
  }

  function onDragMove(e) {
    const info = dragInfo.current;
    if (!info) return;

    // compute current mouse location in same unscaled page coords (but lock to the original page)
    const pos = clientToPageXY(e.clientX, e.clientY);
    // if user drags vertically across pages we still keep the original page for moving
    // so compute currentX/currentY relative to the original page top
    const pageIndex = info.page;
    const container = pageRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const scrollTop = container.scrollTop || 0;

    // compute screen coords for current mouse relative to the start page top
    const pageTopScreen = rect.top - scrollTop + pageIndex * (PAGE_H * scale);
    const currentXScreen = e.clientX - rect.left + (container.scrollLeft || 0);
    const currentYScreen = e.clientY - pageTopScreen;

    const currentX = currentXScreen / scale;
    const currentY = currentYScreen / scale;

    let dx = currentX - info.startMouseX;
    let dy = currentY - info.startMouseY;

    setFields((cur) =>
      cur.map((f) => {
        if (f.id !== info.id) return f;
        let x = info.origX + dx;
        let y = info.origY + dy;
        if (snap) {
          x = Math.round(x / 8) * 8;
          y = Math.round(y / 8) * 8;
        }
        return {
          ...f,
          x: clamp(x, 0, PAGE_W - f.w),
          y: clamp(y, 0, PAGE_H - f.h),
        };
      })
    );
  }

  function endDrag() {
    dragInfo.current = null;
    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", endDrag);
  }

  function updateField(id, patch) {
    setFields((cur) => cur.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  }
  function removeField(id) {
    setFields((cur) => cur.filter((f) => f.id !== id));
    if (activeFieldId === id) setActiveFieldId(null);
  }

  const activeField = fields.find((f) => f.id === activeFieldId) || null;

  // Upload mock (UI only)
  function onUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadName(file.name);
    setPdfFile(URL.createObjectURL(file));
    setLastSavedS3Path(null);
    setFields([]);
    setPages([]); // will be populated on Document load success
  }

  // Save logic using pdf-lib (adjusted to use field.page)
  async function handleSave() {
    if (!pdfFile || fields.length === 0) {
      setSaveStatus({ type: "error", message: "No PDF or fields to save." });
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }

    setSaveStatus({ type: "saving", message: "Processing PDF and uploading to S3..." });

    try {
      const pdfBytes = await fetch(pdfFile).then((res) => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(pdfBytes);

      const form = pdfDoc.getForm();
      const pdfPages = pdfDoc.getPages();

      fields.forEach((f) => {
        // pick corresponding pdf-lib page by index
        const pageIndex = clamp(f.page, 0, pdfPages.length - 1);
        const page = pdfPages[pageIndex];
        const pageHeight = page.getHeight();

        // fields saved using same units as PAGE_W/PAGE_H.
        // Convert top-left y to PDF bottom-left origin
        const y_pdf = pageHeight - (f.y + f.h);

        const rect = [f.x, y_pdf, f.x + f.w, y_pdf + f.h];

        let pdfField;
        const fieldName = `${f.type}_${f.id}`;

        if (f.type === "signature") {
          pdfField = form.createSignatureField(fieldName);
          pdfField.addToPage(page, rect);
        } else {
          pdfField = form.createTextField(fieldName);
          try {
            pdfField.setText(f.placeholder || f.label);
            pdfField.setFontSize(f.fontSize);
            pdfField.updateAppearances();
          } catch (err) {
            // some pdf-lib operations may throw if fonts/appearances aren't available; ignore and still add field
            console.warn("pdf-lib field appearance warning:", err?.message || err);
          }
          pdfField.addToPage(page, rect);
        }

        if (f.required && typeof pdfField.setRequired === "function") {
          try {
            pdfField.setRequired(true);
          } catch (err) {
            // ignore if not supported
          }
        }
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const fileNameToSave = uploadName || "contract_template.pdf";
      const backendResponse = await savePdfToBackend(modifiedPdfBytes, fileNameToSave);

      if (backendResponse.success) {
        setLastSavedS3Path(backendResponse.filePath);
        setSaveStatus({
          type: "success",
          message: `Template saved! Path: ${backendResponse.filePath.substring(0, 40)}...`,
        });
      } else {
        throw new Error("Backend save failed.");
      }
    } catch (error) {
      console.error("Error creating or saving PDF:", error);
      setSaveStatus({ type: "error", message: `Save failed: ${error.message}` });
    } finally {
      setTimeout(() => setSaveStatus(null), 5000);
    }
  }

  async function handleOpenSavedPdf() {
    if (!lastSavedS3Path) {
      setSaveStatus({ type: "error", message: "No saved template path available to open." });
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }

    setSaveStatus({ type: "saving", message: "Retrieving PDF from S3..." });

    try {
      const backendResponse = await getPdfFromBackend(lastSavedS3Path);

      if (backendResponse.success && backendResponse.signedUrl) {
        setPdfFile(backendResponse.signedUrl);
        setFields([]);
        setUploadName(lastSavedS3Path.split("/").pop());
        setSaveStatus({ type: "success", message: "Saved template loaded successfully." });
      } else {
        throw new Error("Could not retrieve signed URL.");
      }
    } catch (error) {
      console.error("Error opening PDF from S3:", error);
      setSaveStatus({ type: "error", message: `Failed to load saved PDF: ${error.message}` });
    } finally {
      setTimeout(() => setSaveStatus(null), 5000);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-50 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950 text-slate-800 dark:text-slate-100">
      {/* Header */}
      <div className="sticky top-0 z-30 backdrop-blur-xl border-b border-white/20 dark:border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 py-4 flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 blur-xl bg-gradient-to-r from-indigo-500/30 via-fuchsia-500/30 to-emerald-500/30 rounded-2xl -z-10" />
            <span className="inline-flex items-center gap-3 rounded-2xl px-4 py-2 bg-white/60 dark:bg-white/5 border border-white/40 dark:border-white/10 shadow-sm">
              <PenLine className="w-5 h-5" />
              <b className="tracking-wide">PDF Template Designer</b>
            </span>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <label className="relative inline-flex items-center overflow-hidden rounded-xl border border-white/40 dark:border-white/10 bg-white/70 dark:bg-white/5 shadow-sm">
              <input type="file" accept="application/pdf" onChange={onUpload} className="hidden" />
              <span className="px-3 py-2 flex items-center gap-2 cursor-pointer">
                <Upload className="w-4 h-4" /> {uploadName || "Upload PDF"}
              </span>
            </label>

            <button
              onClick={handleOpenSavedPdf}
              disabled={!lastSavedS3Path || (saveStatus && saveStatus.type === "saving")}
              className={`px-3 py-2 rounded-xl border border-white/40 dark:border-white/10 bg-white/70 dark:bg-white/5 hover:bg-black/5 dark:hover:bg-white/10 ${
                lastSavedS3Path ? "text-indigo-600" : "text-slate-400"
              }`}
              title={lastSavedS3Path ? `Open saved: ${lastSavedS3Path}` : "No saved PDF available"}
            >
              <FolderOpen className="w-4 h-4" />
            </button>

            <button onClick={() => setGrid((g) => !g)} className={`px-3 py-2 rounded-xl border border-white/40 dark:border-white/10 bg-white/70 dark:bg-white/5 hover:bg-black/5 dark:hover:bg-white/10 ${grid ? "ring-2 ring-indigo-400/50" : ""}`} title="Toggle grid">
              <GridIcon className="w-4 h-4" />
            </button>
            <button onClick={() => setSnap((s) => !s)} className={`px-3 py-2 rounded-xl border border-white/40 dark:border-white/10 bg-white/70 dark:bg-white/5 hover:bg-black/5 dark:hover:bg-white/10 ${snap ? "ring-2 ring-emerald-400/50" : ""}`} title="Toggle snap">
              <Move className="w-4 h-4" />
            </button>
            <button onClick={() => setDark((d) => !d)} className="px-3 py-2 rounded-xl border border-white/40 dark:border-white/10 bg-white/70 dark:bg-white/5 hover:bg-black/5 dark:hover:bg-white/10" title="Theme">
              {dark ? <SunMedium className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            <button
              onClick={handleSave}
              className="ml-2 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white shadow hover:bg-indigo-500"
              disabled={saveStatus && saveStatus.type === "saving"}
            >
              <Save className="w-4 h-4" /> {saveStatus && saveStatus.type === "saving" ? "Saving..." : "Save"}
            </button>
            <button className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white shadow hover:bg-slate-800 dark:bg-white dark:text-slate-900">
              <Eye className="w-4 h-4" /> Preview
            </button>
            <button className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 text-white shadow hover:bg-emerald-400">
              <Send className="w-4 h-4" /> Send
            </button>
          </div>
        </div>
      </div>

      {/* Save Status */}
      <AnimatePresence>
        {saveStatus && (
          <motion.div initial={{ opacity: 0, y: -50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -50 }} className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 rounded-xl px-6 py-3 shadow-2xl flex items-center gap-3 transition-colors ${saveStatus.type === "success" ? "bg-emerald-500 text-white" : saveStatus.type === "error" ? "bg-rose-500 text-white" : "bg-yellow-500 text-slate-900"}`}>
            <CheckCircle className="w-5 h-5" />
            <span>{saveStatus.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Body */}
      <div className="mx-auto max-w-[1400px] px-6 py-6 grid grid-cols-12 gap-6">
        {/* Field Kit */}
        <aside className="col-span-12 md:col-span-3 xl:col-span-2">
          <div className="rounded-3xl border border-white/40 dark:border-white/10 bg-white/60 dark:bg-white/5 backdrop-blur-xl p-3 shadow-lg">
            <h3 className="px-2 pt-1 pb-3 text-sm uppercase tracking-wider text-slate-500 dark:text-slate-400">Field Kit</h3>
            <div className="grid gap-2">
              {FIELD_PRESETS.map((p) => (
                <motion.div key={p.type} layout draggable onDragStart={(e) => onDragStartPalette(e, p)} className="group flex items-center gap-3 rounded-2xl border border-white/40 dark:border-white/10 bg-gradient-to-r from-white/80 to-white/50 dark:from-slate-800/60 dark:to-slate-800/30 px-4 py-3 hover:shadow-xl hover:from-white/90 cursor-grab active:cursor-grabbing">
                  <div className="p-2 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-300"><p.icon className="w-5 h-5" /></div>
                  <div className="font-medium">{p.label}</div>
                  <div className="ml-auto text-xs text-slate-500 opacity-0 group-hover:opacity-100 transition">Drag</div>
                </motion.div>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button className="px-3 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-500">Save Template</button>
              <button className="px-3 py-2 rounded-xl bg-emerald-500 text-white hover:bg-emerald-400">Send</button>
            </div>
          </div>
        </aside>

        {/* Canvas */}
        <main className="col-span-12 md:col-span-6 xl:col-span-8">
          <div className="relative rounded-3xl border border-white/40 dark:border-white/10 bg-white/60 dark:bg-white/5 backdrop-blur-xl p-4 shadow-xl">
            <div className="w-full flex justify-center canvas-viewport">
              <div className="relative select-none shrink-0" style={{ width: scaled.w }}>
                <div
                  onDrop={onDropCanvas}
                  onDragOver={onDragOverCanvas}
                  className="origin-top-left rounded-2xl bg-white dark:bg-slate-900 shadow-2xl ring-1 ring-black/5 relative"
                  style={{ width: scaled.w }}
                >
                  {/* Scrollable container (pageRef) */}
                  <div
                    ref={pageRef}
                    className="pdf-container relative"
                    style={{
                      width: scaled.w,
                   
                      overflowY: "auto",
                      overflowX: "hidden",
                      padding: 12,
                    }}
                  >
                    {pdfFile ? (
                      <Document
                        file={pdfFile}
                        onLoadSuccess={({ numPages }) => {
                          // build pages array with ids
                          setPages(Array.from({ length: numPages }, (_, i) => ({ id: `page_${i + 1}` })));
                        }}
                      >
                        {pages.map((p, index) => (
                          <div key={p.id} className="page-wrapper" style={{ width: scaled.w, height: scaled.h, marginBottom: 16, position: "relative" }}>
                            <Page
                              pageNumber={index + 1}
                              width={scaled.w}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                          </div>
                        ))}
                      </Document>
                    ) : (
                      <div className="p-10 text-slate-400/70 text-lg">Upload PDF to preview</div>
                    )}

                    {/* Render fields inside same scrollable container.
                        Positioning: top = (field.page * PAGE_H + field.y) * scale */}
                    {fields.map((f) => (
                      <FieldBox
                        key={f.id}
                        field={f}
                        active={activeFieldId === f.id}
                        onMouseDown={(e) => startDragField(e, f)}
                        onSelect={() => setActiveFieldId(f.id)}
                        onResize={(patch) => updateField(f.id, patch)}
                        scale={scale}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Page thumbnails */}
            <div className="mt-6 flex gap-3 overflow-x-auto py-2">
              {pages.map((p, idx) => (
                <div key={p.id} className="shrink-0 w-20 h-28 rounded-lg bg-white/80 dark:bg-slate-800/60 border border-white/30 dark:border-white/10 shadow">
                  <div className="text-center text-xs text-slate-500 py-1">{idx + 1}</div>
                </div>
              ))}
              <button className="shrink-0 w-20 h-28 rounded-lg border-2 border-dashed border-indigo-300/70 text-indigo-500/70 text-sm hover:bg-indigo-50/50">+ Page</button>
            </div>
          </div>
        </main>

        {/* Inspector */}
        <aside className="col-span-12 md:col-span-3 xl:col-span-2">
          <div className="rounded-3xl border border-white/40 dark:border-white/10 bg-white/60 dark:bg-white/5 backdrop-blur-xl p-4 shadow-lg">
            <h3 className="text-sm uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">Inspector</h3>
            {!activeField && <div className="text-sm text-slate-500">Select a field to edit properties.</div>}
            {activeField && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-slate-500">Label</label>
                  <input className="mt-1 w-full rounded-xl bg-white/80 dark:bg-slate-900/60 border border-white/40 dark:border-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-400/40" value={activeField.label} onChange={(e) => updateField(activeField.id, { label: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500">Width</label>
                    <input type="number" className="mt-1 w-full rounded-xl bg-white/80 dark:bg-slate-900/60 border border-white/10 px-3 py-2" value={Math.round(activeField.w)} onChange={(e) => updateField(activeField.id, { w: clamp(parseInt(e.target.value || "0"), 40, PAGE_W) })} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Height</label>
                    <input type="number" className="mt-1 w-full rounded-xl bg-white/80 dark:bg-slate-900/60 border border-white/10 px-3 py-2" value={Math.round(activeField.h)} onChange={(e) => updateField(activeField.id, { h: clamp(parseInt(e.target.value || "0"), 24, PAGE_H) })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500">Font size</label>
                    <input type="number" className="mt-1 w-full rounded-xl bg-white/80 dark:bg-slate-900/60 border border-white/10 px-3 py-2" value={activeField.fontSize} onChange={(e) => updateField(activeField.id, { fontSize: clamp(parseInt(e.target.value || "0"), 8, 48) })} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Required</label>
                    <div className="mt-1">
                      <button onClick={() => updateField(activeField.id, { required: !activeField.required })} className={`w-full px-3 py-2 rounded-xl border ${activeField.required ? "bg-emerald-500 text-white border-emerald-500" : "bg-white/80 dark:bg-slate-900/60 border-white/10"}`}>
                        {activeField.required ? "Yes" : "No"}
                      </button>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Color</label>
                  <input type="color" className="mt-1 w-full h-10 rounded-xl overflow-hidden" value={activeField.color} onChange={(e) => updateField(activeField.id, { color: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Placeholder</label>
                  <input className="mt-1 w-full rounded-xl bg-white/80 dark:bg-slate-900/60 border border-white/10 px-3 py-2" value={activeField.placeholder} onChange={(e) => updateField(activeField.id, { placeholder: e.target.value })} />
                </div>
                <div className="pt-2">
                  <button onClick={() => removeField(activeField.id)} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-500 text-white hover:bg-rose-400"><Trash2 className="w-4 h-4" /> Remove</button>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Footer */}
      <div className="mx-auto max-w-[1400px] px-6 pb-8">
        <div className="rounded-3xl border border-white/40 dark:border-white/10 bg-white/60 dark:bg-white/5 backdrop-blur-xl p-4 shadow-lg flex items-center justify-between">
          <div className="text-sm text-slate-500">Tip: Drag from the Field Kit and drop anywhere. Hold and drag to reposition. {snap ? "Snapping is ON" : "Snapping is OFF"}.</div>
          <div className="flex items-center gap-2">
            <button className="px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-500"><Save className="w-4 h-4 inline mr-2" />Save Template</button>
            <button className="px-4 py-2 rounded-xl bg-emerald-500 text-white hover:bg-emerald-400"><Send className="w-4 h-4 inline mr-2" />Send for Signature</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// FieldBox component
function FieldBox({ field, active, onMouseDown, onSelect, onResize, scale = 1 }) {
  const ref = useRef(null);
  const borderClr = active ? "ring-2 ring-indigo-400/70" : "ring-1 ring-black/5";
  const isSignature = field.type === "signature";

  // On-screen positions need to be scaled and offset by page index
  const left = field.x * scale;
  const top = (field.page * PAGE_H + field.y) * scale;
  const width = field.w * scale;
  const height = field.h * scale;

  return (
    <div
      ref={ref}
      onMouseDown={onMouseDown}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.();
      }}
      className={`absolute group ${borderClr} shadow-lg rounded-xl overflow-hidden cursor-move select-none`}
      style={{ left, top, width, height, position: "absolute", zIndex: 50 }}
    >
      <div className="h-full w-full bg-white/90 dark:bg-slate-800/80 backdrop-blur-sm">
        <div className="px-2 pt-1 text-[10px] uppercase tracking-wider text-slate-500 flex items-center justify-between">
          <span>{field.label}</span>
          <span className="text-[9px] opacity-70">{field.required ? "Required" : "Optional"}</span>
        </div>
        <div className="px-2 pb-2 pt-1 h-[calc(100%-18px)]">
          {isSignature ? (
            <div className="h-full flex items-center">
              <div className="w-full border-b-2 border-dashed" style={{ borderColor: field.color }} />
            </div>
          ) : (
            <div className="w-full h-full rounded-md border" style={{ borderColor: field.color, fontSize: field.fontSize }} />
          )}
        </div>
      </div>

      <div className="absolute right-0 bottom-0 w-3 h-3 bg-indigo-500 rounded-br-md cursor-se-resize opacity-0 group-hover:opacity-100"
        onMouseDown={(e) => startResize(e, ref, field, onResize)}
        title="Drag to resize"
      />
    </div>
  );
}

function startResize(e, ref, field, onResize) {
  e.stopPropagation();
  const startX = e.clientX;
  const startY = e.clientY;
  const startW = field.w;
  const startH = field.h;

  // We assume 1:1 scale for resize in PDF units. If you want to support UI scale,
  // you can read the scale value from a prop and divide dx/dy accordingly.
  let scale = 1;

  function move(ev) {
    let dx = (ev.clientX - startX) / scale;
    let dy = (ev.clientY - startY) / scale;
    onResize({ w: Math.max(40, startW + dx), h: Math.max(24, startH + dy) });
  }
  function up() {
    window.removeEventListener("mousemove", move);
    window.removeEventListener("mouseup", up);
  }
  window.addEventListener("mousemove", move);
  window.addEventListener("mouseup", up);
}
